import React from "react";
import { motion } from "framer-motion";
import {
  FaBriefcase,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaFreeCodeCamp,
  FaLinkedin,
  FaLaptopCode,
  FaUniversity,
} from "react-icons/fa";
import { BiCalendar } from "react-icons/bi";
import "./Qualification.css";
import quorteklogo from "../../assets/quortek.png";
// import image from "../../assets/king2.jpg";
import icrc from "../../assets/icrc.png";
import mouau from "../../assets/mouau.png";
import udemy from "../../assets/udemy.jpg";
import linkedin from "../../assets/linkedin-icon.png";

const Qualification = () => {
  const qualifications = {
    experience: [
      {
        id: 1,
        logo: quorteklogo,
        title: "Quortek (Founder & CEO)",
        role: "Full-Stack Developer | AI Engineer",
        period: "2020 - Present",
        description:
          "Leading tech solutions for clients worldwide. Specializing in Web development, Mobile app, Data Analysis, Product Designing and Rebranding and AI integrations. Managed 5+ projects with 95% client satisfaction.",
        tags: ["React", "Node.js", "AI", "Web3", "MongoDB", "NextJs"],
        icon: <FaLaptopCode />,
      },
      {
        id: 2,
        logo: icrc,
        title: "Integral Computing (Instructor)",
        role: "Web Coach",
        period: "2023 - Present",
        description:
          "Teaching modern web development (MERN), mobile apps (React Native), and data analysis (Python/Pandas).",
        tags: ["Teaching", "React", "Python", "Data Analysis"],
        icon: <FaChalkboardTeacher />,
      },
      {
        id: 3,
        logo: linkedin,
        title: "Freelancer",
        role: "Multi-Skill Developer",
        period: "2021 - Present",
        description:
          "A freelancer offering fullstack development, UI/UX design, and data analysis services. 100% job success score across 5+ contracts.",
        tags: ["Freelancing", "Web Dev", "UI/UX", "Consulting"],
        icon: <FaLinkedin />,
      },
      {
        id: 4,
        logo: quorteklogo,
        title: "Online Tech Educator",
        role: "Web & Mobile Instructor",
        period: "2024 - Present",
        description:
          "Coaching and Mentoring Young Devs.  Mentoring students to build real-world applications.",
        tags: ["Online Teaching", "Nextjs", "React", "Figma"],
        icon: <FaFreeCodeCamp />,
      },
    ],
    education: [
      {
        id: 1,
        logo: mouau,
        title: "Michael Okpara University",
        degree: "BSc. Computer Science",
        period: "2019 - 2023",
        description:
          "Specialized in algorithms, data structures, and database management. Graduated with honors while leading multiple tech student projects.",
        tags: ["Algorithms", "Data Structures", "DBMS"],
        icon: <FaUniversity />,
      },
      {
        id: 2,
        logo: icrc,
        title: "Integral Computing",
        degree: "MERN Fullstack Certification",
        period: "2022",
        description:
          "Mastered fullstack development with MongoDB, Express, React, and Node.js. Built 10+ production-ready applications during training.",
        tags: ["MERN", "JavaScript", "APIs"],
        icon: <FaGraduationCap />,
      },
      {
        id: 3,
        logo: udemy,
        title: "Self-Learning Platforms",
        degree: "Advanced UI/UX & Frameworks",
        period: "2023",
        description:
          "Completed 50+ courses on Udemy/FreeCodeCamp covering modern frontend frameworks, design systems, and responsive interfaces.",
        tags: ["Figma", "React", "Vue.js"],
        icon: <FaFreeCodeCamp />,
      },
    ],
  };

  return (
    <section className="qualification-section" id="qualifications">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="section-title"
      >
        My Journey
      </motion.h1>

      <div className="qualification-grid">
        {/* Experience Column */}
        <div className="experience-column">
          <motion.div
            className="section-header"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FaBriefcase className="icon" />
            <h2>Professional Experience</h2>
          </motion.div>

          <div className="timeline">
            {qualifications.experience.map((item) => (
              <ExperienceCard key={item.id} item={item} />
            ))}
          </div>
        </div>

        {/* Education Column */}
        <div className="education-column">
          <motion.div
            className="section-header"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <FaGraduationCap className="icon" />
            <h2>Education</h2>
          </motion.div>

          <div className="timeline">
            {qualifications.education.map((item) => (
              <EducationCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Reusable Card Components
const ExperienceCard = ({ item }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    className="experience-card"
  >
    <div className="card-header">
      <div className="logo-container">
        <img src={item.logo} alt={item.title} />
        <div className="icon-badge">{item.icon}</div>
      </div>
      <div>
        <h3>{item.title}</h3>
        <p className="role">{item.role}</p>
        <p className="period">
          <BiCalendar /> {item.period}
        </p>
      </div>
    </div>
    <p className="description">{item.description}</p>
    <div className="tags-container">
      {item.tags.map((tag, i) => (
        <span key={i} className="tag">
          {tag}
        </span>
      ))}
    </div>
  </motion.div>
);

const EducationCard = ({ item }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    className="education-card"
  >
    <div className="card-header">
      <div className="logo-container">
        <img src={item.logo} alt={item.title} />
        <div className="icon-badge">{item.icon}</div>
      </div>
      <div>
        <h3>{item.title}</h3>
        <p className="degree">{item.degree}</p>
        <p className="period">
          <BiCalendar /> {item.period}
        </p>
      </div>
    </div>
    <p className="description">{item.description}</p>
    <div className="tags-container">
      {item.tags.map((tag, i) => (
        <span key={i} className="tag">
          {tag}
        </span>
      ))}
    </div>
  </motion.div>
);

export default Qualification;
